import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Observable, take, takeUntil } from 'rxjs';
import { RingostatCallService } from 'src/app/ringostat/ringostat-call.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import { Client, ClientPhone, UserRef } from 'src/typings';
import { applyPhonesToClient, formatClientPhone, getClientPhones } from '../../client-phone.util';
import { ClientService } from '../../client.service';
import { CLIENT_DIALOG_CONFIG, CreateComponent, clientPhoneValidator, normalizeClientPhone } from '../../create/create.component';

@Component({
  selector: 'app-main-info',
  templateUrl: './main-info.component.html',
  styleUrls: ['./main-info.component.scss']
})
export class MainInfoComponent implements OnChanges {
  constructor(
    private dialog: MatDialog,
    private clientService: ClientService,
    private notificationService: NotificationService,
    public supportService: SupportService,
    private ringostatCallService: RingostatCallService
  ) {}

  @Input() client?: Client;
  @Output() clientChanged = new EventEmitter<Client>();
  phoneActionLoading = false;

  addPhoneFormControl = new UntypedFormControl('', [clientPhoneValidator]);

  editPhoneFormControl = new UntypedFormControl('', [clientPhoneValidator]);

  responsibleManagerControl = new UntypedFormControl('');

  editingPhoneId: number | null = null;

  responsibleManagerSaving = false;

  callingPhoneId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.client?.currentValue?.id) return;
    this.syncResponsibleManagerControl();
    if (changes.client?.previousValue?.id !== this.client?.id) this.refreshPhones();
  }

  edit() {
    const dialog = this.dialog.open(CreateComponent, CLIENT_DIALOG_CONFIG);
    dialog.componentInstance.client = this.client;
    dialog.componentInstance.clientChanged.pipe(takeUntil(dialog.afterClosed())).subscribe(client => this.publishClient(client));
    dialog.componentInstance.createClient.pipe(take(1), takeUntil(dialog.afterClosed())).subscribe(client => {
      this.publishClient(client); dialog.close();
    });
  }

  private publishClient(client: Client) {
    if (client.id !== this.client?.id) return;
    this.client = { ...this.client, ...client };
    this.clientChanged.emit(this.client);
  }

  get phones(): ClientPhone[] {
    return getClientPhones(this.client);
  }

  formatPhone(phone?: string | null) {
    return formatClientPhone(phone);
  }

  startEditPhone(phone: ClientPhone) {
    this.editingPhoneId = phone.id;
    this.editPhoneFormControl.setValue(phone.phone);
  }

  cancelEditPhone() {
    this.editingPhoneId = null;
    this.editPhoneFormControl.setValue('');
  }

  async refreshPhones() {
    const clientId = this.client?.id;
    if (!clientId) return;
    try {
      const phones = await firstValueFrom(this.clientService.listPhones(clientId));
      if (this.client?.id === clientId) this.publishClient(applyPhonesToClient(this.client, phones));
    } catch (error) {
      if (this.client?.id === clientId) this.notificationService.showError(this.getErrorMessage(error, 'Ошибка загрузки телефонов'));
    }
  }

  private async changePhone(request: () => Observable<ClientPhone[]>, done?: () => void) {
    if (!this.client || this.phoneActionLoading) return;
    const clientId = this.client.id;
    this.phoneActionLoading = true;
    try {
      const phones = await firstValueFrom(request());
      if (this.client?.id === clientId) { this.publishClient(applyPhonesToClient(this.client, phones)); done?.(); }
    } catch (error) { this.notificationService.showError(this.getErrorMessage(error, 'Не удалось сохранить телефон')); }
    finally { this.phoneActionLoading = false; }
  }

  async addPhone() {
    if (!this.client || this.addPhoneFormControl.invalid || !normalizeClientPhone(this.addPhoneFormControl.value)) return;
    await this.changePhone(() => this.clientService.addPhone(this.client!.id, { phone: normalizeClientPhone(this.addPhoneFormControl.value) }), () => this.addPhoneFormControl.setValue(''));
  }

  async savePhone(phoneId: number) {
    if (!this.client || this.editPhoneFormControl.invalid || !normalizeClientPhone(this.editPhoneFormControl.value)) return;
    await this.changePhone(() => this.clientService.updatePhone(this.client!.id, phoneId, { phone: normalizeClientPhone(this.editPhoneFormControl.value) }), () => this.cancelEditPhone());
  }

  async makePrimary(phoneId: number) {
    if (this.client) await this.changePhone(() => this.clientService.setPrimaryPhone(this.client!.id, phoneId));
  }

  async clickToCall(phone: ClientPhone) {
    if (!this.client || this.callingPhoneId !== null) return;

    this.callingPhoneId = phone.id;
    try {
      await firstValueFrom(
        this.ringostatCallService.clickToCall({
          phone: phone.phone,
          clientId: this.client.id,
          clientPhoneId: phone.id
        })
      );
      this.notificationService.notice('Звонок клиенту запущен: после ответа клиента примите вызов');
    } catch (error: unknown) {
      this.notificationService.showError(this.getErrorMessage(error, 'Не удалось запустить звонок'));
    } finally {
      this.callingPhoneId = null;
    }
  }

  async removePhone(phoneId: number) {
    if (!this.client || this.phoneActionLoading || !window.confirm('Удалить номер телефона?')) return;
    await this.changePhone(() => this.clientService.deletePhone(this.client!.id, phoneId), () => {
      if (this.editingPhoneId === phoneId) this.cancelEditPhone();
    });
  }

  async saveResponsibleManager() {
    const manager = this.getSelectedResponsibleManager();
    if (this.responsibleManagerControl.value && !manager) {
      this.notificationService.showError('Выберите менеджера из списка');
      return;
    }

    await this.updateResponsibleManager(manager);
  }

  async clearResponsibleManager() {
    this.responsibleManagerControl.setValue('');
    await this.updateResponsibleManager(null);
  }

  private async updateResponsibleManager(manager: UserRef | null) {
    if (!this.client || this.responsibleManagerSaving) return;

    this.responsibleManagerSaving = true;
    try {
      const response = await firstValueFrom(
        this.clientService.update({
          id: this.client.id,
          responsible_manager_user_id: manager?.id || null
        })
      );

      if (!response.success) {
        this.notificationService.showError(String(response.message || 'Ошибка сохранения менеджера'));
        return;
      }

      this.publishClient({ ...this.client, ...(response.message || {}) });
      this.syncResponsibleManagerControl();
      this.notificationService.notice('Ответственный менеджер сохранен');
    } catch (error: unknown) {
      this.notificationService.showError(this.getErrorMessage(error, 'Ошибка сохранения менеджера'));
    } finally {
      this.responsibleManagerSaving = false;
    }
  }

  private getSelectedResponsibleManager(): UserRef | null {
    const value = this.responsibleManagerControl.value;
    if (value && typeof value === 'object' && 'id' in value) {
      return value as UserRef;
    }
    return null;
  }

  private syncResponsibleManagerControl() {
    this.responsibleManagerControl.setValue(this.client?.responsibleManager || '');
  }

  private getErrorMessage(error: unknown, fallback: string) {
    if (
      typeof error === 'object' &&
      error &&
      'error' in error &&
      typeof (error as { error?: { message?: string } }).error?.message === 'string'
    ) {
      return (error as { error?: { message?: string } }).error?.message || fallback;
    }
    return fallback;
  }
}
